import { Component, computed, effect, HostListener, inject, Injector, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { filter, map, take } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { OktaAuthStateService } from '@okta/okta-angular';

import { AssistantConversationService } from './support/assistant/assistant-conversation.service';
import { AssistantIntroductionService } from './support/assistant/assistant-introduction.service';
import { AssistantPanel } from './support/assistant/assistant-panel';
import { AssistantRuntimeConfigService } from './support/assistant/assistant-runtime-config.service';

import { IconComponent } from './icon';
import { AutofocusDirective } from './core/autofocus.directive';
import { Banner } from './core/banner';
import { OverflowTooltipDirective } from './core/overflow-tooltip.directive';
import { PartnerSwitcher } from './core/partner-switcher';
import { ROLE_CONFIGS, buildRolePath, resolveRoleFromUrl } from './mockup-data';
import { RoleAuthorizationService } from './core/role-authorization.service';
import { SessionTimeoutService } from './core/session-timeout.service';
import { ThemeService } from './core/theme.service';
import { UserProfileService } from './core/user-profile.service';

@Component({
  selector: 'app-root',
  imports: [AssistantPanel, RouterLink, RouterLinkActive, RouterOutlet, IconComponent,
    OverflowTooltipDirective, Banner, PartnerSwitcher, AutofocusDirective],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  protected readonly assistantAuthenticated = signal(false);
  protected readonly assistantRuntime = inject(AssistantRuntimeConfigService);
  private readonly assistantInjector = inject(Injector);
  // Resolve Assistant dependencies only when the Assistant flag enables the feature.
  private get assistantIntroduction(): AssistantIntroductionService | undefined {
    return this.assistantRuntime.enabled() ? this.assistantInjector.get(AssistantIntroductionService) : undefined;
  }
  private get assistantConversation(): AssistantConversationService | undefined {
    return this.assistantRuntime.enabled() ? this.assistantInjector.get(AssistantConversationService) : undefined;
  }
  /** Remembered across reloads, so a narrow sidebar is a preference rather than a per-visit choice. */
  private static readonly NAV_COLLAPSED_KEY = 'hubCentral.navCollapsed';

  private readonly router = inject(Router);
  private readonly authorization = inject(RoleAuthorizationService);
  private readonly authStateService = inject(OktaAuthStateService);
  private readonly themeService = inject(ThemeService);
  protected readonly userProfile = inject(UserProfileService);
  // Injected so the service is instantiated and its expiry timers run; the template reads its signals.
  protected readonly session = inject(SessionTimeoutService);

  /**
   * The colour theme, in a cookie rather than localStorage.
   *
   * Deliberately unlike navCollapsed above, which uses localStorage: a cookie was asked for, and it is also
   * the only one of the two that a server could act on - the theme reaches the origin on every request, so a
   * server-rendered shell could emit the right colours in its first byte. It costs about 24 bytes per
   * request, which is why the sidebar preference is not moved to match.
   */
  protected readonly darkTheme = computed(() => this.themeService.theme() === 'dark');

  /**
   * Whether the sidebar is narrowed to icons.
   *
   * Separate from {@link mobileOpen} because the two are opposite gestures on the same button: below the
   * md breakpoint the sidebar is an overlay drawer that slides in, above it a permanent column that
   * narrows. Each signal is inert at the width where the other applies, so the button can flip both and
   * no viewport detection or resize listener is needed.
   */
  protected readonly navCollapsed = signal(App.restoreNavCollapsed());
  protected readonly mobileOpen = signal(false);
  protected readonly profileOpen = signal(false);
  protected readonly usaBannerExpanded = signal(false);
  protected readonly signingOut = signal(false);
  protected readonly role = signal(this.resolveShellRole(this.router.url));
  protected readonly roleConfig = computed(() => ROLE_CONFIGS[this.role()]);
  protected readonly roleBasePath = computed(() => buildRolePath(this.role()));

  /** The signed-in user's display name, read from the id token. */
  /** Keep the application shell hidden until Okta confirms an authenticated session.
   * The router outlet stays mounted so Okta's callback and redirect can complete.
   */
  protected readonly onOAuthCallback = computed(() => this.roleUrl() === '/login/callback' || this.roleUrl() === '/signed-out');
  protected readonly roleUrl = signal(this.router.url.split(/[?#]/)[0]);
  protected readonly assistantBottomClearance = signal(104);
  protected readonly userFlow = computed(() => this.authenticated()
    && (this.roleUrl() === '/user' || this.roleUrl().startsWith('/user/')));
  protected readonly approverFlow = computed(() => this.authenticated()
    && (this.roleUrl() === '/approver' || this.roleUrl().startsWith('/approver/')));
  protected readonly adminFlow = computed(() => this.authenticated()
    && (this.roleUrl() === '/admin' || this.roleUrl().startsWith('/admin/')));
  protected readonly assistantDocked = computed(() => (this.userFlow() || this.approverFlow() || this.adminFlow())
    && this.assistantRuntime.enabled());

  protected readonly authenticated = toSignal(
    this.authStateService.authState$.pipe(map((state) => state?.isAuthenticated === true)),
    { initialValue: false },
  );

  protected readonly signedInName = toSignal(
    this.authStateService.authState$.pipe(
      map((state) => {
        const claims = state?.idToken?.claims;
        return claims?.name ?? claims?.preferred_username ?? claims?.email ?? 'User';
      }),
    ),
    { initialValue: 'User' },
  );

  protected readonly signedInInitials = computed(() =>
    this.signedInName()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase(),
  );

  constructor() {
    // Clear chat state when authentication expires, including without an explicit sign-out.
    this.authStateService.authState$
      .pipe(takeUntilDestroyed())
      .subscribe((state) => {
        // Null means the SDK is still restoring authentication, not a logout.
        if (state == null) return;
        this.assistantAuthenticated.set(Boolean(state.isAuthenticated));
        if (!state.isAuthenticated) {
          this.assistantConversation?.reset();
          this.assistantIntroduction?.endLogin();
        }
      });
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event) => {
        this.role.set(this.resolveShellRole(event.urlAfterRedirects));
        this.roleUrl.set(event.urlAfterRedirects.split(/[?#]/)[0]);
        // The mobile drawer closes on navigation because it covers the page. navCollapsed deliberately
        // survives: re-expanding the sidebar on every nav click would make collapsing pointless.
        this.mobileOpen.set(false);
        this.profileOpen.set(false);
      });

    // The IDM profile is only needed in the user (partner) flow — it supplies the partnerId the approver
    // and admin areas don't use — so fetch it once after login only for a user who holds the user role.
    this.authStateService.authState$
      .pipe(
        filter((state) => Boolean(state?.isAuthenticated)),
        take(1),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        if (this.authorization.hasRole('user')) {
          this.userProfile.load().subscribe();
        }
      });

    effect(() => App.storeNavCollapsed(this.navCollapsed()));
  }

  /**
   * Reads the remembered preference. Storage can throw when cookies are blocked or the browser is in a
   * restricted mode, and any failure here should leave the sidebar expanded rather than break the shell.
   */
  private static restoreNavCollapsed(): boolean {
    try {
      return localStorage.getItem(App.NAV_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  }

  private static storeNavCollapsed(collapsed: boolean): void {
    try {
      localStorage.setItem(App.NAV_COLLAPSED_KEY, String(collapsed));
    } catch {
      // A layout preference is not worth surfacing an error for; it simply will not persist.
    }
  }

  @HostListener('document:click', ['$event'])
  protected closeProfileMenu(event: MouseEvent): void {
    if (event.target instanceof Element && event.target.closest('.app-header__profile')) return;

    this.profileOpen.set(false);
  }

  @HostListener('document:keydown.escape')
  protected closeProfileMenuOnEscape(): void {
    this.profileOpen.set(false);
  }

  private resolveShellRole(url: string) {
    const firstSegment = url.replace(/^\/+/, '').split(/[/?#]/)[0].toLowerCase();
    if (firstSegment === 'user' || firstSegment === 'approver' || firstSegment === 'admin') {
      return resolveRoleFromUrl(url);
    }

    return this.authorization.defaultRole() ?? 'user';
  }

  /** One button, two behaviours: the drawer below the md breakpoint, the narrow column above it. */
  protected toggleNavigation(): void {
    this.mobileOpen.update((value) => !value);
    this.navCollapsed.update((value) => !value);
  }

  protected closeNavigation(): void {
    this.mobileOpen.set(false);
  }

  protected toggleProfileMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.profileOpen.update((value) => !value);
  }

  /** Toggles the CMS-style government-site guidance without leaving Hub Central. */
  protected toggleUsaBanner(): void {
    this.usaBannerExpanded.update((value) => !value);
  }

  /**
   * Flips the theme and leaves the menu open, so the effect of the switch is visible where it was thrown.
   * Closing on click - as Sign out does - would hide the control at the moment it changed.
   */
  protected toggleTheme(event: Event): void {
    event.stopPropagation();
    this.themeService.toggle();
  }

  protected async signOut(): Promise<void> {
    if (this.signingOut()) return;
    this.profileOpen.set(false);
    this.signingOut.set(true);
    this.assistantConversation?.reset();
    await this.session.logout();
  }
}
